import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ScheduledLessonHomeworkProps } from '@/components/scheduled-lesson/scheduled-lesson-homework/ScheduledLessonHomework';
import { useSingleCache } from '@/data/hook/query/v2/useCacheManager';
import useListService from '@/data/hook/useListService';
import {
  createHomeworkActivity,
  deleteHomeworkActivity,
  updateHomeworkActivity,
} from '@/data/services/homeworkActivityServices';
import { createScheduledLessonMgActivity } from '@/data/services/mgActivityServices';
import {
  homeworkActivitiesQueryKeys,
  scheduledLessonsQueryKeys,
} from '@/data/services/querykeys';
import { AssignmentType } from '@/models/HomeworkActivity';
import ScheduledLesson from '@/models/ScheduledLesson';
import { getErrorMessage } from '@/utils/getErrorMessage';
import alert from '@/utils/shared/alert';

export default function useScheduledLessonHomework({
  scheduledLessonId,
  homeworkActivityId,
  hasHomeworkActivity,
}: ScheduledLessonHomeworkProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'scheduledLessonReportModal',
  });

  const { queryKey } = scheduledLessonsQueryKeys.get(scheduledLessonId);
  const lessonCache = useSingleCache<ScheduledLesson>(queryKey);

  const { results, isInitialLoading, cacheManager } = useListService({
    ...homeworkActivitiesQueryKeys.list({
      activity: homeworkActivityId ?? 0,
    }),
    enabled: hasHomeworkActivity && !!homeworkActivityId,
  });

  const homework = results.at(0);

  const ensureMgActivityId = async () => {
    if (homeworkActivityId) return homeworkActivityId;

    const mgActivity = await createScheduledLessonMgActivity(scheduledLessonId);
    lessonCache.merge({ homeworkActivity: mgActivity.id });
    return mgActivity.id;
  };

  const createMutation = useMutation({
    mutationFn: async (content: string) => {
      const activity = await ensureMgActivityId();
      return createHomeworkActivity({
        activity,
        content,
        assignmentType: AssignmentType.NOTHING,
        isActive: true,
      });
    },
    onSuccess: exam => {
      cacheManager.add(exam);
      alert.success(t('homeworkCreated'));
    },
    onError: err => alert.error(getErrorMessage(err)),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, content }: { id: number; content: string }) =>
      updateHomeworkActivity(id, { content }),
    onSuccess: updated => {
      cacheManager.update(updated);
      alert.success(t('homeworkUpdated'));
    },
    onError: err => alert.error(getErrorMessage(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteHomeworkActivity(id),
    onSuccess: (_, id) => {
      cacheManager.remove(id);
      alert.success(t('homeworkDeleted'));
    },
    onError: err => alert.error(getErrorMessage(err)),
  });

  return {
    homework,
    isLoading: isInitialLoading,
    createHomework: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    updateHomework: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    deleteHomework: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
  };
}
